/* =========================================================================
   LUXURY MOTION ENGINE — Phase 1
   Lightweight, dependency-free progressive enhancement:
   - scroll reveal (IntersectionObserver)
   - header scrolled state
   - subtle parallax on hero media
   - product card 3D tilt (desktop, pointer devices only)
   - image fade-in on load
   - page fade-in on first paint
   Does not touch cart/variant/checkout logic — purely visual layer.
   ========================================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;

  /* ---------- Page fade-in ---------- */
  function pageIn() {
    document.body.classList.add('lux-loaded');
  }
  if (document.readyState === 'complete') {
    pageIn();
  } else {
    window.addEventListener('load', pageIn);
    // Fallback in case load event is delayed
    setTimeout(pageIn, 900);
  }

  /* ---------- Auto-tag elements for scroll reveal ---------- */
  function tagRevealTargets() {
    var selectors = [
      'section.shopify-section',
      '.product-grid-item',
      '.grid__item',
      '.banner_list_item',
      '.testimonial-item',
      '.multicolumn-card',
      '.footer__blocks-wrapper > *'
    ];
    var seen = new WeakSet();

    selectors.forEach(function (sel) {
      var nodes = document.querySelectorAll(sel);
      nodes.forEach(function (el) {
        if (
          seen.has(el) ||
          el.hasAttribute('data-lux-reveal') ||
          el.closest('header') ||
          el.closest('.header') ||
          el.closest('.shopify-section-group-header-group') ||
          el.closest('.shopify-section-group-footer-group') ||
          el.closest('.swiper, .swiper-slide, [class*="slideshow"]') ||
          el.querySelector('header')
        ) return;
        // Avoid tagging the very first hero/slideshow section so it's visible immediately
        var firstMainSection = document.querySelector('main section.shopify-section, main .shopify-section');
        if (el.matches('section.shopify-section') && el.querySelector('[class*="slideshow"]') && el === firstMainSection) {
          return;
        }
        seen.add(el);
        el.setAttribute('data-lux-reveal', 'up');
      });
    });

    // Stagger product/card grids as groups
    document.querySelectorAll('.grid, .collection-list, ul.product-grid, .swiper-wrapper').forEach(function (grid) {
      if (!grid.hasAttribute('data-lux-stagger') && grid.children.length > 1 && grid.children.length <= 24) {
        grid.setAttribute('data-lux-stagger', '');
      }
    });
  }

  function initRevealObserver() {
    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('[data-lux-reveal], [data-lux-stagger]').forEach(function (el) {
        el.classList.add('lux-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('lux-in');
            io.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    document.querySelectorAll('[data-lux-reveal], [data-lux-stagger]').forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------- Header scroll state ---------- */
  function initHeaderScroll() {
    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      var y = window.scrollY;
      document.documentElement.classList.toggle('lux-scrolled', y > 40);
      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------- Subtle parallax on hero / banner media ---------- */
  function initParallax() {
    if (reduceMotion || isTouch) return;
    var targets = document.querySelectorAll(
      '.banner_list_item img, .collection-hero__image-container img'
    );
    if (!targets.length) return;

    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      targets.forEach(function (img) {
        var rect = img.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        var progress = (rect.top / vh) - 0.5; // -0.5 .. 0.5 roughly
        var offset = progress * 24; // px
        img.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0) scale(1.08)';
      });
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------- Product card 3D tilt (pointer devices only) ---------- */
  function initCardTilt() {
    if (reduceMotion || isTouch) return;
    var cards = document.querySelectorAll('.product-grid-item');
    cards.forEach(function (card) {
      var frame = null;
      card.addEventListener('mousemove', function (e) {
        if (frame) return;
        frame = window.requestAnimationFrame(function () {
          var rect = card.getBoundingClientRect();
          var px = (e.clientX - rect.left) / rect.width - 0.5;
          var py = (e.clientY - rect.top) / rect.height - 0.5;
          card.style.transform = 'perspective(900px) rotateX(' + (py * -3.5).toFixed(2) + 'deg) rotateY(' + (px * 3.5).toFixed(2) + 'deg) translateY(-6px)';
          frame = null;
        });
      });
      card.addEventListener('mouseleave', function () {
        card.style.transform = '';
      });
    });
  }

  /* ---------- Image fade-in on decode ---------- */
  function initImageFade() {
    document.querySelectorAll('img').forEach(function (img) {
      if (img.hasAttribute('data-lux-img')) return;
      // Never touch slideshow/swiper/hero images — carousels can delay or
      // skip the native `load` event, which would leave the image stuck
      // invisible. These must always render immediately.
      if (img.closest('.swiper, .swiper-slide, [class*="slideshow"], .banner_list_item, .collection-hero')) return;
      img.setAttribute('data-lux-img', '');
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('lux-img-in');
      } else {
        img.addEventListener('load', function () {
          img.classList.add('lux-img-in');
        });
        // Safety net: force-show after 1.2s even if load never fires
        setTimeout(function () { img.classList.add('lux-img-in'); }, 1200);
      }
    });
  }

  /* ---------- Re-scan on Shopify section reloads (theme editor) & dynamic content ---------- */
  function rescan() {
    tagRevealTargets();
    initRevealObserver();
    initImageFade();
  }

  document.addEventListener('DOMContentLoaded', function () {
    tagRevealTargets();
    initRevealObserver();
    initHeaderScroll();
    initParallax();
    initCardTilt();
    initImageFade();
  });

  document.addEventListener('shopify:section:load', rescan);
  document.addEventListener('cart:updated', initImageFade);

  // MutationObserver to catch swiper/dynamically-injected slides + quick-add renders
  var mo = new MutationObserver(function (mutations) {
    var shouldRescan = mutations.some(function (m) {
      return m.addedNodes && m.addedNodes.length > 0;
    });
    if (shouldRescan) {
      clearTimeout(window.__luxRescanTimer);
      window.__luxRescanTimer = setTimeout(rescan, 250);
    }
  });
  document.addEventListener('DOMContentLoaded', function () {
    mo.observe(document.body, { childList: true, subtree: true });
  });
})();
